/* eslint-disable */
import { ChevronLeft, Info } from 'lucide-react'
import { useEffect, useState, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import PlaceOrderBg from '../../assets/images/PlaceOrderBg.png'
import { Label } from '../../Components/ui/label'
import { Input } from '../../Components/ui/input'
import { Button } from '../../Components/ui/button'
import { Card } from '../../Components/ui/card'
import { AccountBalance } from '@mui/icons-material'
import { useModal } from '../../hooks/use-modal-store'
import { useErrorPopup } from '../../hooks/useErrorPopup'
import PostHogTracker from '../../utils/PostHogTracker'
import env from '../../env/env'
import { post } from '../../http/restservice'
import { useRecoilState, useRecoilValueLoadable } from 'recoil'
import { BankDetails } from '../../store/AccountDetailsStore'
import { userDetailsState } from '../LoginState'
import { formatIndianNumber } from '../../utils/Util'
import { toCurrency } from '../../utils/CurrencyUtil'
import OrderOtpVerification from '../FirstIdeas/OrderOtpVerification'

const MFBasketRedeem = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { onOpen } = useModal()
  const { showError } = useErrorPopup()
  const [openOTPPopup, setOpenOTPPopup] = useState(false)
  const [basket] = useState(location?.state?.basket || [])
  const [userDetails] = useRecoilState(userDetailsState)
  const email = userDetails?.email
  const { contents: bankDetails } = useRecoilValueLoadable(BankDetails)
  const [redemptionPercentage, setRedemptionPercentage] = useState(100)
  const [redemptionAmount, setRedemptionAmount] = useState(0) // precise value (can have decimals)
  const [redemptionAmountDisplay, setRedemptionAmountDisplay] = useState('0') // shown in input (no decimals)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [src, setSrc] = useState('')

  const clientCode = userDetails?.uid?.split('-')[0]

  // ---- Derived Values ----
  const totalCurrentAmount = useMemo(() => {
    return basket.reduce((acc, plan) => acc + plan.currentNav * plan.netUnits, 0).toFixed(3)
  }, [basket])

  useEffect(() => {
    const preciseAmount = Number(totalCurrentAmount) || 0
    setRedemptionAmount(preciseAmount)
    setRedemptionAmountDisplay(String(Math.floor(preciseAmount)))
  }, [totalCurrentAmount])

  // ---- Bank Logo ----
  useEffect(() => {
    if (bankDetails?.ifscCode) {
      const extractedBranch = bankDetails.ifscCode.match(/^[A-Za-z]{4}/)?.[0]?.toLowerCase()
      setSrc(`${env.BANK_LOGOS}/${extractedBranch}/symbol.svg`)
    }
  }, [bankDetails])

  const handleError = () => setSrc(null)

  // ---- Helpers ----
  const showPopup = (title, message, severity = 'info', admin = false) => {
    onOpen(admin ? 'openAdminPopup' : 'openPopup', {
      title,
      message,
      severity,
      redirectBackTo: '/dashboard/mutual-funds/investments',
      forwardTo: '/dashboard/mutual-funds',
    })
  }

  const handleRedemptionAmountChange = (e) => {
    setError('')
    const value = e.target.value.replace(/[^0-9]/g, '') // Only allow digits for display
    setRedemptionAmountDisplay(value)
    if (!isNaN(value) && Number(value) >= 0) {
      setRedemptionAmount(Number(value)) // internal stays numeric (can be decimal in other flows)
    }
  }

  const handleRedemptionPercentage = (btn) => {
    setRedemptionPercentage(btn)
    const amount = Number(totalCurrentAmount) || 0 // ensure numeric

    const percentage = Number(btn)
    const calculatedAmount = (amount * percentage) / 100
    setRedemptionAmount(calculatedAmount) // keep precise value internally
    setRedemptionAmountDisplay(String(Math.floor(calculatedAmount))) // show whole number
  }

  // ---- API Handler ----
  const handleBasketRedeem = async () => {
    setError('')

    // Validation
    // if (!redemptionAmount || Number(redemptionAmount) <= 0) {
    //   return setError('Please enter a valid redemption amount')
    // }

    if (Number(redemptionAmount) > Number(totalCurrentAmount)) {
      return setError('Entered amount exceeds available balance')
    }

    try {
      setIsLoading(true)

      const redemptionUrl = `${env.API_URL}mutualfundservice/api/basket/redemptionFunds`
      const redemptionPayload = {
        customerBasketInvestmentId: basket[0]?.customerBasketInvestmentId,
        universalBasketId: basket[0]?.universalBasketId || 1,
        withdrawableAmount: Math.round(Number(redemptionAmount)),
      }

      const redemptionResponse = await post(redemptionUrl, redemptionPayload)
      if (redemptionResponse?.isError || !redemptionResponse?.length) {
        showError(
          'Redemption Failed!',
          redemptionResponse?.message ||
          'There was an issue with redemption. Please try again later. For assistance, contact us at +91-8050034800.',
          () => { },
        )
        return
      }

      const orderData = redemptionResponse.map((fund) => ({
        ...fund,
        isAllRedeem:
          fund.totalAllotedUnits.toFixed(2) === fund.withdrawableUnits.toFixed(2) ? 'Y' : 'N',
      }))

      const orderPayload = {
        clientCode: clientCode?.toUpperCase(),
        phoneNumber: formatIndianNumber(userDetails?.phoneNumber),
        customerBasketInvestmentId: basket[0]?.customerBasketInvestmentId,
        orderData,
      }

      const basketRedeem = await post(
        `${env.API_URL}mutualfundservice/api/basket/oneTimeRedemptionOrder`,
        orderPayload,
      )

      if (basketRedeem?.isError || basketRedeem?.status === 'FAILURE') {
        showError(
          'Redemption Failed!',
          basketRedeem?.message ||
          'There was an issue with redemption. Please try again later. For assistance, contact us at +91-8050034800.',
          () => { },
        )
        return
      }

      if (basketRedeem?.status === 'SUCCESS') {
        const successOrders = basketRedeem.orders?.filter((o) => o?.status === 'SUCCESS')
        if (successOrders?.length === basketRedeem.orders?.length) {
          showPopup(
            'Redemption Initiated!',
            'Your redemption request has been successfully initiated. Redemption will be completed in 1–5 working days.',
            'success',
          )
          return
        }
      }
    } catch (err) {
      console.error('Redemption API Error:', err)
      showError(
        'Redemption Failed!',
        'There was an issue with redemption. Please try again later. For assistance, contact us at +91-8050034800.',
        () => { },
      )
    } finally {
      setIsLoading(false)
    }
  }

  const generateOTP = async () => {
    const response = await post(
      `${env.API_URL}mutualfundservice/api/mutualFund/sendTransactionalOtp`,
      {
        phoneNumber: userDetails?.phoneNumber,
        email: email.includes('fabits') ? '' : email,
      },
    )

    if (response.isError) {
      PostHogTracker.buttonClicked('Order OTP Resend Failed', {
        action: 'Order OTP Resend Failed',
        page_name: 'Action Center Orders Page',
      })
      showError('OTP Error', 'Failed to generate OTP. Please try again later.', () => { })
    }

    if (!response.isError) {
      PostHogTracker.buttonClicked('Order OTP Resend Success', {
        action: 'Order OTP Resend Successful',
        page_name: 'Action Center Orders Page',
      })
      setOpenOTPPopup(true)
    }
  }

  // ---- Render ----
  return (
    <>
      <div
        className='flex gap-x-1 items-center font-semibold cursor-pointer lg:py-2.5 p-4 lg:px-0'
        onClick={() => navigate(-1)}
      >
        <ChevronLeft className='h-5 w-5' />
        Redeem
      </div>

      <div className='max-w-[420px] mx-auto flex flex-col gap-y-6 px-4'>
        {/* Basket Header */}
        {/* <div className='h-[200px] relative flex justify-center items-center border'>
            <div
              className='absolute inset-0 bg-cover'
              style={{ backgroundImage: `url(${PlaceOrderBg})` }}
            />
            <div className='absolute inset-0 bg-gradient-to-b from-white to-transparent' />
            <div className='flex flex-col justify-normal items-center text-center space-x-4 z-10'>
              <img
                src={`${env.SVGS}/basket.svg`}
                alt={basket[0]?.customerBasketName}
                className='w-12 h-12'
              />
              <h3 className='font-semibold text-sm w-48'>{basket[0]?.customerBasketName}</h3>
            </div>
          </div>  */}

        <div className='h-[200px] relative flex justify-center items-center shadow-sm border rounded-md'>
          <div className='flex flex-col justify-center items-center text-center w-full'>
            <img
              src={`${env.SVGS}/basket.svg`}
              alt={basket[0]?.customerBasketName}
              className='w-12 h-12'
            />
            <h3 className='font-semibold text-xl'>{basket[0]?.customerBasketName}</h3>

            <div className='flex flex-col items-center justify-center mt-2'>
              <span className='text-sm text-black-400'> Available Balance</span>
              <span className='font-semibold text-primary-500 text-2xl'>
                ₹ {toCurrency(Math.floor(totalCurrentAmount))}
              </span>
            </div>
          </div>
        </div>

        <div className='flex flex-col space-y-6 p-4 rounded-md border'>
          {/* Input */}
          <div className='flex flex-col items-start gap-1.5'>
            <Label htmlFor='amount' className='text-black-400 text-sm'>
              Withdrawal Amount
            </Label>
            <Input
              startAdornment='₹'
              type='amount'
              id='amount'
              placeholder='Amount'
              className='w-full'
              value={redemptionAmountDisplay}
              onChange={handleRedemptionAmountChange}
            />

            {error && <p className='text-center text-sm text-error-500'>{error}</p>}

            <div className='grid grid-cols-3 gap-x-2 mt-2 w-full'>
              {[10, 25, 50].map((btn, index) => {
                return (
                  <Button
                    key={`${btn}-${index}`}
                    size='simple'
                    className={`p-2 rounded-md shadow-sm ${btn === redemptionPercentage
                      ? 'bg-gray-50 border text-black hover:bg-gray-50'
                      : 'bg-gray-50 text-black-500 hover:bg-gray-50'
                      }`}
                    onClick={() => handleRedemptionPercentage(btn)}
                  >
                    {btn === 100 ? 'Full' : `${btn}%`}
                  </Button>
                )
              })}
            </div>

            <div className='grid grid-cols-3 gap-x-2 w-full'>
              {[75, 100].map((btn, index) => {
                return (
                  <Button
                    key={`${btn}-${index}`}
                    size='simple'
                    className={`p-2 rounded-md shadow-sm ${btn === 100 ? 'col-span-2' : ''} ${btn === redemptionPercentage
                      ? 'bg-gray-50 border text-black hover:bg-gray-50'
                      : 'bg-gray-50 text-black-500 hover:bg-gray-50'
                      }`}
                    onClick={() => handleRedemptionPercentage(btn)}
                  >
                    {btn === 100 ? 'Full' : `${btn}%`}
                  </Button>
                )
              })}
            </div>
          </div>

          {/* Bank Details */}
          {/* <Card className='flex items-center gap-3 p-4 bg-white shadow-none border-[#D3DBEC] rounded-2xl'>
              <div className='flex items-center justify-center w-10 h-10 rounded-full border'>
                {src ? (
                  <img src={src} alt='bank logo' onError={handleError} height={24} width={24} />
                ) : (
                  <AccountBalance className='text-primary-200 w-5 h-5' />
                )}
              </div>
              <div>
                <h3 className='font-semibold text-black-500'>{bankDetails?.accountHolderName}</h3>
                <p className='text-sm text-black-400'>
                  <span className='font-semibold'>{bankDetails?.bankName}</span>
                  <span> A/c ending in </span>
                  <span className='font-semibold'>{bankDetails?.accountNo?.slice(-4)}</span>
                </p>
              </div>
            </Card> */}
        </div>

        {/* More Information */}
        <div className='bg-warning-50 dark:bg-warning-950/20 rounded-lg p-4'>
          <div className='flex items-start gap-3'>
            <div className='space-y-1'>
              <h5 className='font-medium text-warning-900 dark:text-warning-100 flex items-center gap-x-1'>
                {/* <Info className='h-4 w-4 text-warning-900 dark:text-warning-100' /> */}
                Important Notice
              </h5>
              <p className='text-sm text-warning-700 dark:text-warning-300'>
                Withdrawn funds will be transferred to your linked bank account within 1-5 business
                days.
              </p>
            </div>
          </div>
        </div>

        {/* Redeem Button */}
        <Button
          className='rounded-md bg-primary-500 hover:bg-primary-400 disabled:bg-primary-200'
          loading={isLoading}
          onClick={generateOTP}
          disabled={
            isLoading || redemptionAmount > Number(totalCurrentAmount) || redemptionAmount <= 0
          }
        >
          Continue Withdrawal
        </Button>
      </div>

      <OrderOtpVerification
        phoneNumber={userDetails?.phoneNumber}
        openOTPPopup={openOTPPopup}
        setOpenOTPPopup={setOpenOTPPopup}
        email={userDetails?.email}
        handleSipOrOT={handleBasketRedeem}
      />
    </>
  )
}

export default MFBasketRedeem
